<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { computed, inject, ref } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import { getNestedPropertyValue } from 'lib/utils/object.util';

import {
  DialogConfirmEmits,
  DialogConfirmProps,
  DialogConfirmSlots,
} from './DialogConfirm.vue.d';
import Button from '../button/Button.vue';
import Icon from '../icon/Icon.vue';

const props = withDefaults(defineProps<DialogConfirmProps>(), {
  list: undefined,
  listLabel: undefined,
  closeLabel: undefined,
  closeAfterConfirm: true,
  actionable: true,
  showIcon: true,
  showCloseButton: true,
  confirmSeverity: undefined,
  disableConfirmButton: false,
});

const emit = defineEmits<DialogConfirmEmits>();

defineSlots<DialogConfirmSlots>();

const DialogPreset = inject<Record<string, any>>('preset', {}).dialog;

const DialogConfirmPreset = inject<Record<string, any>>(
  'preset',
  {},
).dialogconfirm;

const { locale } = useWangsVueConfig('DialogConfirm');

const dialogConfirm = ref<Dialog>();

const closeButtonLabel = computed<string>(() => {
  if (props.closeLabel) return props.closeLabel;
  return props.actionable
    ? locale.actionableCloseButtonLabel
    : locale.nonActionableCloseButtonLabel;
});

const closeDialog = (): void => {
  emit('update:visible', false);
  emit('close');
};

const confirm = (): void => {
  emit('confirm');
  if (props.closeAfterConfirm) emit('update:visible', false);
};
</script>

<template>
  <Dialog
    ref="dialogConfirm"
    :closable="false"
    :draggable="false"
    :header="header"
    :pt="{
      root: {
        'class': [
          ...DialogPreset?.root({ state: {} }).class,
          ...DialogConfirmPreset?.root.class,
        ],
        'data-wv-section': DialogConfirmPreset?.root['data-wv-section'](
          $attrs['data-wv-section'],
        ),
        'data-wv-name': DialogConfirmPreset?.root['data-wv-name'](
          $attrs['data-wv-name'],
        ),
      },
      header: {
        ...DialogConfirmPreset?.header,
        class: [
          ...DialogPreset?.header.class,
          DialogConfirmPreset?.header.class,
        ],
      },
      content: {
        ...DialogConfirmPreset?.content,
        class: [
          ...DialogPreset?.content({ state: {}, instance: {} }).class,
          DialogConfirmPreset?.content.class,
          props.contentClass,
        ],
      },
      footer: {
        ...DialogConfirmPreset?.footer,
        class: [
          ...DialogPreset?.footer.class,
          DialogConfirmPreset?.footer.class,
        ],
      },
    }"
    :visible="props.visible"
    @hide="emit('hide')"
    @update:visible="emit('update:visible', !!$event)"
    close-on-escape
    modal
  >
    <template #header>
      <Icon
        v-if="showIcon"
        v-bind="DialogConfirmPreset?.headericon({ props })"
        :severity="severity"
        aria-label="Header Icon"
      />

      <h3 v-bind="DialogConfirmPreset?.headertitle(severity)">
        {{ header }}
      </h3>
    </template>

    <template #default>
      <ul v-if="list" v-bind="DialogConfirmPreset?.list">
        <li :key="index" v-for="(item, index) in list">
          {{
            listLabel && typeof item !== 'string'
              ? getNestedPropertyValue(item, listLabel)
              : item
          }}
        </li>
      </ul>

      <slot :items="list" name="body">
        <p v-bind="DialogConfirmPreset?.bodymessage">
          {{ message ?? locale.defaultConfirmMessage }}
        </p>
      </slot>
    </template>

    <template #footer>
      <slot name="footer">
        <Button
          v-if="showCloseButton"
          v-bind="DialogConfirmPreset?.cancelbutton"
          :label="closeButtonLabel"
          @click="closeDialog"
          severity="secondary"
          text
        />

        <Button
          v-if="actionable"
          v-focus
          v-bind="DialogConfirmPreset?.confirmbutton"
          :disabled="disableConfirmButton"
          :label="confirmLabel ?? locale.defaultConfirmLabel"
          :severity="confirmButtonSeverity ?? severity"
          @click="confirm"
        />
      </slot>
    </template>
  </Dialog>
</template>
